import { ToolKind, ToolUse } from "bundles/BigqueryQueryViewer/store/chat";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Choices } from "./choices";
import { YesNo } from "./yesno";
import { OkCancel } from "./okcancel";
import I18n from "lib/I18n";
import { Badge, Spinner } from "react-bootstrap";
import { useState } from "react";
import { BsChevronDown, BsChevronRight } from "react-icons/bs";
import * as styles from "./style.module.css";
import MonacoEditor from "@monaco-editor/react";

interface ToolUseComponentProps {
  toolUse: ToolUse;
  result?: string;
  isLast: boolean;
  onToolResponse: (tool: ToolUse, response: unknown) => void;
}

export const ToolUseComponent = observer((props: ToolUseComponentProps) => {
  const { toolUse, result, isLast, onToolResponse } = props;
  const [showResult, setShowResult] = useState(false);
  switch (toolUse.toolKind) {
    case ToolKind.choices:
      return (
        <Choices
          toolUse={toolUse}
          isLast={isLast}
          onToolResponse={onToolResponse}
        />
      );
    case ToolKind.yesNo:
      return (
        <YesNo
          toolUse={toolUse}
          isLast={isLast}
          onToolResponse={onToolResponse}
        />
      );
    case ToolKind.okCancel:
      return (
        <OkCancel
          toolUse={toolUse}
          isLast={isLast}
          onToolResponse={onToolResponse}
        />
      );
    default:
      return (
        <div className={styles.toolResultRoot}>
          <Badge
            as="button"
            type="button"
            bg="secondary"
            className={styles.toolResultBadge}
            disabled={result == undefined}
            aria-expanded={showResult}
            title={
              result == undefined
                ? undefined
                : I18n.t("bigquery_query_viewer.label.expand_json")
            }
            onClick={() => setShowResult((show) => !show)}
          >
            {result == undefined ? (
              <Spinner
                animation="border"
                size="sm"
                className={styles.toolResultSpinner}
              />
            ) : showResult ? (
              <BsChevronDown />
            ) : (
              <BsChevronRight />
            )}
            {I18n.t("bigquery_query_viewer.label.tool_use", {
              tool: toolUse.toolKind,
            })}
          </Badge>
          {showResult && result != undefined && <ToolResult result={result} />}
        </div>
      );
  }
});

const formattedToolResult = (result: string) => {
  try {
    return JSON.stringify(JSON.parse(result), null, 2);
  } catch {
    return result;
  }
};

const ToolResult = (props: { result: string }) => {
  const value = formattedToolResult(props.result);
  const height = Math.min(
    Math.max(value.split("\n").length * 19 + 16, 80),
    320,
  );

  return (
    <div className={styles.toolResult}>
      <MonacoEditor
        value={value}
        height={height}
        width="100%"
        language="json"
        options={{
          automaticLayout: true,
          readOnly: true,
          domReadOnly: true,
          scrollBeyondLastLine: false,
          wordWrap: "on",
          minimap: { enabled: false },
          overviewRulerLanes: 0,
          renderLineHighlight: "none",
          tabSize: 2,
        }}
      />
    </div>
  );
};
